import { isProductionLike } from '@realtyfeed/shared-types';
import type { ResourcesConfig } from 'aws-amplify';
import { Amplify } from 'aws-amplify';
import type { CognitoStackConfig, ConfigureAuthOptions } from './type';

/**
 * Cognito coordinates for RF Backend, ported from the legacy `src/core/utils/aws-exports.ts`.
 *
 * ### These are NOT the sibling workspace's values
 *
 * This file arrived with the `libs/shared/auth` copy carrying `rf-mls-vendor-dashboard-v2`'s
 * stacks, and the two products are not interchangeable. They share the **user pools** but not
 * the app clients, not the prod identity pool, and not the prod GraphQL endpoint.
 *
 * The app client is the dangerous one. The API gateway authorizer rejects a token minted
 * against a client it does not expect, so the whole console 403s with a session that otherwise
 * looks perfectly valid — the sibling workspace shipped exactly that bug when its two portals
 * were merged onto one config. Verified against the legacy `dev` branch:
 *
 * | stage         | user pool             | app client                    |
 * | ------------- | --------------------- | ----------------------------- |
 * | prod, staging | `us-east-1_YwEZzgqw9` | `594git832a3pmi74bb0m3jf3k4`  |
 * | dev, devTest  | `us-east-1_92trFqjMo` | `3v9u7eek40rqqkacg1a6uu6v8n`  |
 *
 * ### Why they are in source and not `.env`
 *
 * Pool and client identifiers are public by design — they appear in every signed-in browser's
 * network tab. They are deployment configuration, not secrets. Changing one repoints every user
 * of that stage, so change them only with the backend team.
 *
 * ### Two stacks, not four
 *
 * `prod` and `staging` share one; `dev` and `devTest` share the other. That is the backend's
 * arrangement, matching the legacy file's own `RUN_MODE !== Prod && RUN_MODE !== Stage` split.
 *
 * ### No hosted UI
 *
 * The legacy app authenticates with username and password through the Amplify SDK. It has no
 * `loginWith.oauth` block, no `signInWithRedirect` call site and no configured redirect URLs.
 * The copied config declared an OAuth domain and redirect URLs; that was carried for a flow
 * this product does not use, and has been dropped rather than pointed at a domain nobody has
 * set up. If hosted UI is ever wanted, it is a deliberate change with the backend team.
 */
const STACKS = {
  prod: {
    identityPoolId: 'us-east-1:71527184-6f01-465a-8783-44628c5aca6b',
    userPoolId: 'us-east-1_YwEZzgqw9',
    userPoolClientId: '594git832a3pmi74bb0m3jf3k4',
    graphqlEndpoint:
      'https://i6mvieo3jfc7hgjy6x6khzilni.appsync-api.us-east-1.amazonaws.com/graphql',
  },
  dev: {
    identityPoolId: 'us-east-1:76389feb-e688-4ab2-83e5-b5dc1068d507',
    userPoolId: 'us-east-1_92trFqjMo',
    userPoolClientId: '3v9u7eek40rqqkacg1a6uu6v8n',
    graphqlEndpoint:
      'https://i6mvieo3jfc7hgjy6x6khzilni.appsync-api.us-east-1.amazonaws.com/graphql',
  },
} as const satisfies Record<string, CognitoStackConfig>;

const buildConfig = ({
  identityPoolId,
  userPoolId,
  userPoolClientId,
  graphqlEndpoint,
  region = 'us-east-1',
}: CognitoStackConfig): ResourcesConfig => ({
  Auth: {
    Cognito: {
      identityPoolId,
      userPoolId,
      userPoolClientId,
    },
  },
  API: {
    GraphQL: {
      endpoint: graphqlEndpoint,
      region,
      defaultAuthMode: 'userPool',
    },
  },
});

/** Which Cognito stack a run mode selects. Exported so a test can assert the split. */
export const stackFor = (runMode: string | undefined): keyof typeof STACKS =>
  isProductionLike(runMode) ? 'prod' : 'dev';

/**
 * Configures Amplify. Called once in `main.tsx`, before anything can ask for a token.
 *
 * There is no `portal` argument: this product is a single console, so there is one app client
 * per stage rather than one per portal.
 */
export const configureAuth = ({ runMode }: ConfigureAuthOptions): void => {
  Amplify.configure(buildConfig(STACKS[stackFor(runMode)]));
};
